import type { AIVoiceFilters } from 'js/src/data/ai-voices-and-avatars/ai-voice-modal/ai-voice-modal-slice';
import type { ClipCreatorPage } from 'js/src/data/clip-creator/slices/clip-creator-pages-slice';

export const SFX_FEEDBACK_INTERACTION_THRESHOLD = 3;

// Prompt for Composer feedback once the user picks Composer in the create-video
// choice modal this many times.
export const COMPOSER_FEEDBACK_CHOICE_THRESHOLD = 2;

// Show the Agent vs Composer choice modal on the first "new video" click and
// every this many clicks after that. In between, the click goes straight to the
// flow the user picked last.
export const CREATE_VIDEO_CHOICE_CLICK_INTERVAL = 10;

export const LocalStorageKeys = {
    DISABLE_AUTO_DOWNLOAD: 'DISABLE_AUTO_DOWNLOAD',
    COMMENTER_USERNAME: 'LOCAL_STORAGE_USERNAME_KEY',
    COMMENTER_EMAIL: 'LOCAL_STORAGE_EMAIL_KEY',
    COLOR_PICKER_RECENT_COLORS: 'l5_colorpicker',
    AI_AVATAR_UPSELL_SHOWN: 'AI_AVATAR_UPSELL_SHOWN',
    GUIDE_TOGGLE_BUTTON: 'scene_design_editor:guide_status',
    GRID_TOGGLE_BUTTON: 'scene_design_editor:grid_status',
    TRANSITION_TOGGLE_BUTTON: 'scene_design_editor:transition_status',
    WATERMARK_TOGGLE_BUTTON: 'scene_design_editor:watermark_status',
    COPIED_MEDIA_ASSET: 'COPIED_MEDIA_ASSET',
    CLIP_CREATOR_ENTRY_TIME: 'clipCreatorEntryTime',
    CLIP_CREATOR_LAST_PAGE: 'clipCreatorLastPage',
    SFX_FEEDBACK_INTERACTION_COUNT: 'sfxFeedbackInteractionCount',
    SFX_FEEDBACK_DISMISSED: 'sfxFeedbackDismissed',
    YOUTUBE_CHANNEL_CALLOUT_DISMISSED: 'YOUTUBE_CHANNEL_CALLOUT_DISMISSED',
    ANIMATION_EXIT_FEEDBACK: 'animationExitFeedback',
    ANIMATION_DEEPEST_STAGE: 'animationDeepestStage',
    ANIMATION_PAGE_VISIT_COUNT: 'animationPageVisitCountV2',
    ANIMATION_HAS_REACHED_GENERATE: 'animationHasReachedGenerate',
    ANIMATION_MINUTES_LOW_BALANCE_SHOWN_FOR_CYCLE: 'animationMinutesLowBalanceShownForCycle',
    ANIMATION_COMPLETION_TOAST_SHOWN_VIDEO_IDS: 'animationCompletionToastShownVideoIds',
    AI_VOICE_FILTERS: 'aiVoiceFilters',
    EMAIL_CAPTURE_DISMISSED: 'emailCaptureDismissed',
    CREATE_MATCH_ROW_SHOWN: 'createMatchRowShown',
    COMPOSER_FEEDBACK_CHOICE_COUNT: 'composerFeedbackChoiceCount',
    COMPOSER_FEEDBACK_ARMED: 'composerFeedbackArmed',
    COMPOSER_FEEDBACK_DISMISSED: 'composerFeedbackDismissed',
    CREATE_VIDEO_CHOICE_CLICK_COUNT: 'createVideoChoiceClickCount',
    CREATE_VIDEO_CHOICE_LAST_CHOICE: 'createVideoChoiceLastChoice',
} as const;

export const ANIMATION_EXIT_FEEDBACK_VISIT_INTERVAL = 10;

type LocalStorageValues = {
    [LocalStorageKeys.DISABLE_AUTO_DOWNLOAD]: boolean;
    [LocalStorageKeys.COLOR_PICKER_RECENT_COLORS]: string[];
    [LocalStorageKeys.COMMENTER_USERNAME]: string;
    [LocalStorageKeys.COMMENTER_EMAIL]: string;
    [LocalStorageKeys.GUIDE_TOGGLE_BUTTON]: boolean;
    [LocalStorageKeys.GRID_TOGGLE_BUTTON]: boolean;
    [LocalStorageKeys.TRANSITION_TOGGLE_BUTTON]: boolean;
    [LocalStorageKeys.WATERMARK_TOGGLE_BUTTON]: boolean;
    [LocalStorageKeys.AI_AVATAR_UPSELL_SHOWN]: boolean;
    [LocalStorageKeys.COPIED_MEDIA_ASSET]: string;
    [LocalStorageKeys.CLIP_CREATOR_ENTRY_TIME]: number;
    [LocalStorageKeys.CLIP_CREATOR_LAST_PAGE]: {
        lastPage: ClipCreatorPage;
        feedback: boolean;
        timestamp: number;
    };
    [LocalStorageKeys.SFX_FEEDBACK_INTERACTION_COUNT]: number;
    [LocalStorageKeys.SFX_FEEDBACK_DISMISSED]: boolean;
    [LocalStorageKeys.YOUTUBE_CHANNEL_CALLOUT_DISMISSED]: boolean;
    [LocalStorageKeys.ANIMATION_EXIT_FEEDBACK]: {
        genxStage: string;
        openedInCreator: boolean;
        genxSessionId: string | null;
    };
    [LocalStorageKeys.ANIMATION_DEEPEST_STAGE]: string;
    [LocalStorageKeys.ANIMATION_PAGE_VISIT_COUNT]: number;
    [LocalStorageKeys.ANIMATION_HAS_REACHED_GENERATE]: boolean;
    [LocalStorageKeys.ANIMATION_MINUTES_LOW_BALANCE_SHOWN_FOR_CYCLE]: string;
    [LocalStorageKeys.ANIMATION_COMPLETION_TOAST_SHOWN_VIDEO_IDS]: number[];
    [LocalStorageKeys.AI_VOICE_FILTERS]: Omit<AIVoiceFilters, 'page'>;
    [LocalStorageKeys.EMAIL_CAPTURE_DISMISSED]: string[];
    /** Whether the Create page's match row showed last visit, so it can hold its space while loading. */
    [LocalStorageKeys.CREATE_MATCH_ROW_SHOWN]: boolean;
    [LocalStorageKeys.COMPOSER_FEEDBACK_CHOICE_COUNT]: number;
    [LocalStorageKeys.COMPOSER_FEEDBACK_ARMED]: boolean;
    [LocalStorageKeys.COMPOSER_FEEDBACK_DISMISSED]: boolean;
    [LocalStorageKeys.CREATE_VIDEO_CHOICE_CLICK_COUNT]: number;
    [LocalStorageKeys.CREATE_VIDEO_CHOICE_LAST_CHOICE]: 'agent' | 'composer';
};

export const LOCAL_STORAGE_USER_ID = 'LOCAL_STORAGE_USER_ID';

function warnStorageUnavailable(error: unknown): void {
    // eslint-disable-next-line no-console
    console.warn('Local storage is not available.', error);
}

function clearLocalStorage(win: Window): void {
    const keys = Object.values(LocalStorageKeys);
    for (const key of keys) {
        win.localStorage.removeItem(key);
    }
}

function validateLocalStorage(win: Window): void {
    const userId = win.SERVER?.USER?.id;
    const currentUserId = userId === undefined || userId === null ? null : `${userId}`;
    const storedUserId = win.localStorage.getItem(LOCAL_STORAGE_USER_ID);
    if (currentUserId === storedUserId) {
        return;
    }
    clearLocalStorage(win);
    if (currentUserId) {
        win.localStorage.setItem(LOCAL_STORAGE_USER_ID, currentUserId);
    } else {
        win.localStorage.removeItem(LOCAL_STORAGE_USER_ID);
    }
}

function readStoredString(key: string, win: Window): string | null {
    try {
        validateLocalStorage(win);
        return win.localStorage.getItem(key);
    } catch (error) {
        warnStorageUnavailable(error);
        return null;
    }
}

/**
 * Store a value in localStorage, with type checking.
 */
export function localStorageSet<K extends keyof LocalStorageValues>(
    key: K,
    value: LocalStorageValues[K],
    win?: Window,
): void {
    if (!win) {
        win = window;
    }
    try {
        validateLocalStorage(win);
        win.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        warnStorageUnavailable(error);
    }
}

/**
 * Retrieve a value from localStorage, with type checking.
 */
export function localStorageGet<K extends keyof LocalStorageValues>(
    key: K,
    win?: Window,
): LocalStorageValues[K] | null {
    if (!win) {
        win = window;
    }
    const stringValue = readStoredString(key, win);
    if (stringValue === null) {
        return null;
    }
    try {
        return JSON.parse(stringValue) as LocalStorageValues[K];
    } catch (error) {
        if (
            key === LocalStorageKeys.COMMENTER_USERNAME ||
            key === LocalStorageKeys.COMMENTER_EMAIL
        ) {
            // These values used to be stored as a string, so this offers backwards compatibility
            // After some time, so browsers can update, we can remove this again (added Aug 2024).
            return stringValue as LocalStorageValues[K];
        }
        // eslint-disable-next-line no-console
        console.warn(
            `Failed to parse value from localStorage for key: ${key}, value: ${stringValue}`,
            error,
        );
        return null;
    }
}

export function localStorageRemove<K extends keyof LocalStorageValues>(key: K, win?: Window): void {
    if (!win) {
        win = window;
    }
    try {
        win.localStorage.removeItem(key);
    } catch (error) {
        warnStorageUnavailable(error);
    }
}
